import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme, alpha, type Theme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TimelineIcon from '@mui/icons-material/Timeline';

import { extractErrorMessage } from '../api/errors';
import { metricDescription, prettyMetric } from '../util/metricLabels';
import { getCohortTiers, type CohortTier } from '../api/cohortBaseline';
import {
  getMySessionReport,
  getSessionReport,
  requestMySessionReport,
  requestSessionReport,
} from '../api/reports';
import {
  getMySessionLongitudinal,
  getMySessionLongitudinalReport,
  getParticipantProcedureLongitudinal,
  getSessionLongitudinal,
  getSessionLongitudinalReport,
  requestMySessionLongitudinalReport,
  requestSessionLongitudinalReport,
  type LongitudinalReport,
} from '../api/longitudinal';
import {
  getMySessionMetricComparison,
  getSessionMetricComparison,
  type MetricComparisonRow,
  type SessionMetricComparison,
} from '../api/sessionMetrics';
import { usePreparedDownload } from '../hooks/usePreparedDownload';
import { PreparedDownloadNote } from './PreparedDownloadNote';
import {
  LongitudinalCompareView,
  LongitudinalReportView,
} from './LongitudinalReportView';
import { listParticipants, type Participant } from '../api/tenants';

/**
 * Per-session metric scorecard (master-baseline-cohort-submit follow-on).
 * Between the Activity timeline and the cognitive trace on the participant
 * drill-down: a compact grid (4 across) of per-metric **gauges** — a "You"
 * marker against whichever reference cohorts we hold (novice → expert,
 * from master_baselines) — grouped into Motion + EEG sections. Cohort
 * labels + colours come from the canonical tier registry (GET
 * /cohort-tiers). Metrics with no mapped reference just show the "You"
 * marker. Double-click a card to zoom it.
 */

// "You" is a crisp BLACK dot sitting on a bold ORANGE bar (`YOU_ACCENT`).
// The orange bar draws the eye to the participant's position; the black dot
// (with a white ring so it separates from the orange and stays visible on
// the dark-theme track) marks the exact value. Both are FIXED across every
// metric — deliberately not the old per-metric performance colour — so a
// card never reads "green here, blue there"; status lives only in the
// verdict chip. Cohort ticks keep their tier colours (novice purple /
// practice blue).
const YOU_COLOR = '#111111'; // the "You" dot (reads pure black)
const YOU_ACCENT = '#f57c00'; // the bold orange bar / fill / halo
const NEUTRAL_COLOR = '#9e9e9e'; // non-tier cohorts (e.g. the EEG `other`)

/**
 * Section glyph — a recoloured PNG silhouette (EEG head / motion hand) drawn
 * as a CSS mask so it takes the brand `primary.main` in both themes and the
 * cut-out details (gear, finger gaps) show through as transparency. Larger
 * than the old MUI icons per the Version-D scorecard refresh.
 */
function MetricGlyph({ src, size = 42 }: { src: string; size?: number }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        bgcolor: 'primary.main',
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
      }}
    />
  );
}

// Fallback until GET /cohort-tiers resolves (or if it fails) — mirrors the
// backend cohort_tiers.COHORT_TIERS so labels/colours are correct offline.
const FALLBACK_TIERS: CohortTier[] = [
  { key: 'novice', label: 'Novice', rank: 0, color: '#3f51b5' },
  { key: 'practice_ready', label: 'Practice-ready', rank: 1, color: '#7986cb' },
  { key: 'expert', label: 'Expert', rank: 2, color: '#4c8dff' },
];

// Legacy free-text cohort synonyms → canonical tier key (mirrors
// cohort_tiers.LEGACY_ALIASES) so un-normalised baseline rows still map to
// a standard label/colour. The EEG population baseline `other` is
// deliberately NOT a skill tier — it gets its own "Others" label below.
const LEGACY_ALIASES: Record<string, string> = {
  not_practice_ready: 'novice',
  beginner: 'novice',
  trainee: 'practice_ready',
  competent: 'practice_ready',
  proficient: 'practice_ready',
  attending: 'expert',
  faculty: 'expert',
  mastery: 'expert',
};

const NON_TIER_LABEL: Record<string, string> = { other: 'Others' };

// Gauge marker colours per canonical tier. The registry's tier colours are
// three near-identical indigos (novice #3f51b5 / practice-ready #7986cb /
// expert #4c8dff), so two reference lines on one track were almost
// impossible to tell apart. We deliberately override them here with three
// clearly different hues — purple → blue → green, all distinct from the
// orange "You" and legible for the common red-green colour-blind types.
// Labels still come from the registry (the single source of truth); only
// the swatch colour is ours. Legend dot + track marker share this value, so
// a line still maps to its legend entry by colour.
const TIER_MARKER_COLOR: Record<string, string> = {
  novice: '#ab47bc', // purple
  practice_ready: '#42a5f5', // blue
  expert: '#66bb6a', // green
};

// Performance tiers (Version D · Pass 2). Rather than a flat pass/fail against
// a single reference, we grade "You" on a four-step ladder so that *getting
// better* reads as a distinct, rewarding step-up — not just "green vs amber".
//
//   bad   — below practice-ready            → warning (orange)
//   good  — at/above practice-ready         → success (green)
//   great — at/above expert                 → deep green
//   elite — clearly beyond the top cohort   → blue
//
// Computed from raw values + direction (not the 0-100 gauge position, whose
// domain stretches with an out-of-band value and would collapse "elite").
export type Tier = 'bad' | 'good' | 'great' | 'elite';

export function performanceTier(
  you: number | null,
  refs: { value: number }[],
  direction: string,
): Tier | null {
  if (you === null || refs.length === 0 || direction === 'none') return null;
  const meets = (v: number, ref: number) =>
    direction === 'lower' ? v <= ref : v >= ref;
  // Order references worst → best, so [last] is the top cohort (expert) and
  // [second-last] is the practice-ready threshold.
  const sorted = [...refs].sort((a, b) =>
    direction === 'lower' ? b.value - a.value : a.value - b.value,
  );
  const best = sorted[sorted.length - 1].value;
  const second = sorted.length >= 2 ? sorted[sorted.length - 2].value : best;
  if (meets(you, best)) {
    if (sorted.length < 2) return 'good'; // single reference: no expert step
    // Elite = beating the top cohort by more than ¾ of the practice→expert
    // gap, i.e. visibly out ahead rather than just nudging past it.
    const gap = Math.abs(best - second) || Math.abs(best) * 0.15 || 1;
    const beyond = direction === 'lower' ? best - you : you - best;
    return beyond > gap * 0.75 ? 'elite' : 'great';
  }
  if (meets(you, second)) return 'good';
  return 'bad';
}

function tierColor(theme: Theme, tier: Tier | null): string {
  switch (tier) {
    case 'bad':
      return theme.palette.warning.main;
    case 'good':
      return theme.palette.success.main;
    case 'great':
      return theme.palette.success.dark;
    case 'elite':
      return theme.palette.info.main;
    default:
      return YOU_COLOR;
  }
}

const TIER_LABEL: Record<Tier, string> = {
  bad: 'Below practice-ready',
  good: 'Practice-ready',
  great: 'Expert level',
  elite: 'Elite',
};

/** Resolve a (possibly legacy) cohort key to its standard label + a
 *  distinguishable marker colour, using the canonical registry for the
 *  label and our tier palette for the colour. */
function makeCohortResolver(tiers: CohortTier[]) {
  const byKey = new Map(tiers.map((t) => [t.key, t]));
  const resolve = (cohort: string) => {
    const canonical = LEGACY_ALIASES[cohort] ?? cohort;
    const tier = byKey.get(canonical);
    if (tier) {
      return {
        label: tier.label,
        color: TIER_MARKER_COLOR[canonical] ?? tier.color,
      };
    }
    const label =
      NON_TIER_LABEL[cohort] ??
      cohort.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return { label, color: NEUTRAL_COLOR };
  };
  return resolve;
}

type CohortResolver = ReturnType<typeof makeCohortResolver>;

// prettyMetric lives in util/metricLabels so this card and the tenant
// Settings → Metrics visibility tab render every key identically.

function fmtValue(v: number, unit: string | null): string {
  if (unit === 's') return `${Math.round(v)}s`;
  // Fraction-valued metrics (idle/active) are stored 0–1; show as a percent.
  if (unit === '%') return `${Math.round(v * 100)}%`;
  return String(Number(v.toFixed(3)));
}

/** A dot + label + value used in the gauge legend. */
function LegendItem({
  color,
  label,
  value,
  big,
  outline,
  faded,
}: {
  color: string;
  label: string;
  value: string;
  big?: boolean;
  /** Draw a grey ring around the dot — used for the white "You" swatch. */
  outline?: boolean;
  /** De-emphasise a non-significant (indicative) reference. */
  faded?: boolean;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={faded ? { opacity: 0.55 } : undefined}
    >
      <Box
        sx={{
          width: big ? 16 : 12,
          height: big ? 16 : 12,
          borderRadius: '50%',
          bgcolor: color,
          border: outline ? '1.5px solid' : 'none',
          borderColor: 'text.secondary',
          flexShrink: 0,
        }}
      />
      <Typography
        component="span"
        sx={{
          fontSize: big ? '0.92rem' : '0.72rem',
          color: 'text.secondary',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: big ? '0.92rem' : '0.72rem',
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

/** A horizontal gauge: a track with the "You" fill + tier tick-markers,
 *  and a legend (You + each cohort). `big` renders the zoomed dialog
 *  version. Replaces the old 3-bar chart — one shared axis makes "where do
 *  I sit vs novice/practice-ready/expert" read at a glance, and the flex
 *  legend keeps every label aligned to its marker at any size. */
function Gauge({
  row,
  resolve,
  big = false,
}: {
  row: MetricComparisonRow;
  resolve: CohortResolver;
  big?: boolean;
}) {
  const theme = useTheme();
  const you = row.session_value;
  const refs = row.references;

  // Black "You" dot on a bold orange bar. `youRing` is a light ring that both
  // separates the black dot from the orange fill and keeps it visible on the
  // dark-theme track. `accent` is the bold orange; `neutral` (grey) is for the
  // secondary median line so it doesn't compete with the black hero dot.
  const youColor = YOU_COLOR; // black dot
  const youRing = theme.palette.common.white; // light separator ring
  const accent = YOU_ACCENT; // bold orange bar / fill / box
  // Orange intensity encodes significance: the bar/box are the SAME bold
  // orange for a real (statistically significant) benchmark; they drop to the
  // de-emphasised, less-saturated orange only when the metric's references
  // exist but none are significant (indicative-only — matches the faded ticks).
  const nonSigMetric =
    refs.length > 0 && refs.every((r) => r.significant === false);
  const fillOpacity = nonSigMetric ? 0.4 : 0.9; // bar
  const boxAlpha = nonSigMetric ? 0.5 : 0.85; // IQR box fill
  if (you === null && refs.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        no data
      </Typography>
    );
  }

  const dist = row.distribution ?? null;

  const values = [
    ...(you !== null ? [you] : []),
    ...refs.map((r) => r.value),
    // Frame to the WHISKERS, not min/max/outliers: a single stray outlier
    // (e.g. a 20-min duration among ~30-s recordings) would otherwise set the
    // axis max and crush the box into a 1–2 % sliver. Outliers still render —
    // pos() clamps them to the track edge — they just don't dictate the scale.
    ...(dist ? [dist.whisker_low, dist.whisker_high] : []),
  ];
  // Frame the axis to the data (with a small pad) rather than anchoring at 0:
  // ratio metrics (e.g. Focus ~4–5.6) would otherwise compress into the right
  // third and the box plot would be unreadable. `values` spans the cohort refs
  // + the distribution's in-fence range, so the box always lands inside.
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const pad = (hi - lo || Math.abs(hi) || 1) * 0.08;
  const domainMin = lo - pad;
  const domainMax = hi + pad;
  const span = domainMax - domainMin || 1;
  const rawPct = (v: number) =>
    Math.max(0, Math.min(100, ((v - domainMin) / span) * 100));

  // Orientation: EVERY gauge reads worse (left) → better (right). For a
  // lower-is-better metric we flip the axis so the good end (expert /
  // practice-ready) still lands on the right — otherwise those markers,
  // and the green "You" fill, would jump to the left and the eye would have
  // to re-learn each card. No-direction metrics keep their natural low→high
  // axis (there's no "better" to point at).
  const isLower = row.direction === 'lower';
  const directional = row.direction === 'higher' || row.direction === 'lower';
  const pos = (v: number) => (isLower ? 100 - rawPct(v) : rawPct(v));

  const trackH = big ? 22 : 14;
  const youPct = you !== null ? pos(you) : null;

  // Legend entries sorted by their marker position on the track (low→high
  // value = left→right), so the label sequence mirrors the tick sequence.
  // A stable sort keeps You ahead of a cohort sharing its exact value.
  const legendEntries = [
    ...(you !== null
      ? [
          {
            key: '__you',
            label: 'You',
            color: youColor,
            value: you,
            pos: youPct as number,
            outline: true,
            faded: false,
          },
        ]
      : []),
    ...refs.map((r) => {
      const { label, color } = resolve(r.cohort);
      return {
        key: r.cohort,
        label,
        color,
        value: r.value,
        pos: pos(r.value),
        outline: false,
        faded: r.significant === false,
      };
    }),
  ].sort((a, b) => a.pos - b.pos);

  return (
    <Box sx={{ mt: big ? 2 : 1 }}>
      {/* "You" now renders as a bold vertical bar on the track itself
          (below), consistent with the cohort markers. A small spacer keeps
          the bar's top overhang clear of the row above. */}
      <Box sx={{ height: big ? 6 : 4 }} />

      {/* Track: light rail, faint orange fill up to You, cohort ticks. */}
      <Box
        sx={{
          position: 'relative',
          height: trackH,
          borderRadius: `${trackH / 2}px`,
          bgcolor: 'action.hover',
        }}
      >
        {/* Back layer. No distribution → a neutral fill up to You (shows how
            far along the axis You sits; status lives in the verdict chip, not
            the fill). With a distribution → the participant's whisker range +
            IQR box ("this session vs your range"). */}
        {!dist && youPct !== null && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${youPct}%`,
              bgcolor: accent,
              opacity: fillOpacity, // bold unless the benchmark isn't significant
              borderRadius: 'inherit',
            }}
          />
        )}
        {dist &&
          (() => {
            const wL = Math.min(pos(dist.whisker_low), pos(dist.whisker_high));
            const wR = Math.max(pos(dist.whisker_low), pos(dist.whisker_high));
            const bL = Math.min(pos(dist.q1), pos(dist.q3));
            const bR = Math.max(pos(dist.q1), pos(dist.q3));
            return (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    left: `${wL}%`,
                    width: `${wR - wL}%`,
                    height: 2,
                    bgcolor: 'text.secondary',
                    opacity: 0.55,
                  }}
                />
                <Tooltip
                  title={`Your middle 50%: ${fmtValue(dist.q1, row.unit)}–${fmtValue(dist.q3, row.unit)} · n=${dist.n}`}
                >
                  <Box
                    data-testid="boxplot-iqr"
                    sx={{
                      position: 'absolute',
                      top: big ? 3 : 1,
                      bottom: big ? 3 : 1,
                      left: `${bL}%`,
                      width: `${bR - bL}%`,
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: accent,
                      // Same bold orange as the single-bar view — this IS the
                      // "orange bar" for the distribution (your middle-50%
                      // range). Drops to the faded orange only when the
                      // benchmark isn't statistically significant.
                      bgcolor: alpha(accent, boxAlpha),
                    }}
                  />
                </Tooltip>
              </>
            );
          })()}
        {refs.map((r) => {
          const { label, color } = resolve(r.cohort);
          // Non-significant references are shown as CONTEXT only (option 3):
          // a faded, dashed tick that never drives the verdict. The tooltip
          // says so.
          const nonSig = r.significant === false;
          return (
            <Tooltip
              key={r.cohort}
              title={
                nonSig
                  ? `${label}: ${fmtValue(r.value, row.unit)} · indicative (not statistically significant)`
                  : `${label}: ${fmtValue(r.value, row.unit)}`
              }
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -4,
                  bottom: -4,
                  left: `${pos(r.value)}%`,
                  transform: 'translateX(-50%)',
                  width: big ? 12 : 7,
                  borderRadius: 1,
                  // Significant → solid filled tick. Non-significant → a faded,
                  // dashed vertical bar so it reads as "context, not a claim".
                  ...(nonSig
                    ? {
                        bgcolor: 'transparent',
                        opacity: 0.5,
                        backgroundImage: `repeating-linear-gradient(180deg, ${color} 0 3px, transparent 3px 6px)`,
                      }
                    : { bgcolor: color }),
                  // A ring in the card background separates the line from the
                  // track and from an adjacent same-family marker.
                  boxShadow: (t) => `0 0 0 1.5px ${t.palette.background.paper}`,
                }}
              />
            </Tooltip>
          );
        })}
        {/* median of the participant's range */}
        {dist && (
          <Tooltip title={`Your median: ${fmtValue(dist.median, row.unit)}`}>
            <Box
              data-testid="boxplot-median"
              sx={{
                position: 'absolute',
                // Contained WITHIN the IQR box (matches its inset) so it reads
                // as a median divider on the orange, not a gap slicing through
                // the bar. Darker-orange line, no light ring.
                top: big ? 3 : 1,
                bottom: big ? 3 : 1,
                left: `${pos(dist.median)}%`,
                transform: 'translateX(-50%)',
                width: big ? 6 : 4,
                bgcolor: accent,
                filter: 'brightness(0.6)',
                borderRadius: 1,
              }}
            />
          </Tooltip>
        )}
        {/* outlier sessions beyond the Tukey fences */}
        {dist &&
          dist.outliers.map((o, i) => (
            <Tooltip key={`o-${i}`} title={`Outlier session: ${fmtValue(o, row.unit)}`}>
              <Box
                data-testid="boxplot-outlier"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: `${pos(o)}%`,
                  transform: 'translate(-50%, -50%)',
                  width: big ? 9 : 7,
                  height: big ? 9 : 7,
                  borderRadius: '50%',
                  border: '1.5px solid',
                  borderColor: 'text.secondary',
                  bgcolor: 'background.paper',
                }}
              />
            </Tooltip>
          ))}
        {/* "You" — this session. Against a distribution it's the hero DOT
            (today vs your range); without one it's the bold vertical bar. */}
        {youPct !== null &&
          (dist ? (
            <Tooltip title={`This session: ${fmtValue(you as number, row.unit)}`}>
              <Box
                data-testid="boxplot-today"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: `${youPct}%`,
                  transform: 'translate(-50%, -50%)',
                  width: big ? 17 : 13,
                  height: big ? 17 : 13,
                  borderRadius: '50%',
                  bgcolor: youColor,
                  // White ring only — the dot sits on the bold orange IQR box,
                  // so an orange halo would just blend in.
                  boxShadow: `0 0 0 2.4px ${youRing}`,
                  zIndex: 2,
                }}
              />
            </Tooltip>
          ) : (
            <Box
              data-testid="you-bar"
              sx={{
                position: 'absolute',
                top: -6,
                bottom: -6,
                left: `${youPct}%`,
                transform: 'translateX(-50%)',
                width: big ? 14 : 9,
                bgcolor: youColor,
                borderRadius: 1,
                // White bar + grey outline so it reads on the light track
                // (the old ring used the card colour, invisible against white).
                boxShadow: `0 0 0 1.5px ${youRing}`,
                zIndex: 2,
              }}
            />
          ))}
      </Box>

      {/* Orientation cue — every directional gauge reads worse (left) →
          better (right), so the flip on lower-is-better metrics is
          self-explanatory. Skipped for no-direction metrics (no "better"). */}
      {directional && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 0.25,
            px: 0.25,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: big ? '0.82rem' : '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: 'text.secondary',
              lineHeight: 1,
            }}
          >
            ‹ worse
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: big ? '0.82rem' : '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: 'text.secondary',
              lineHeight: 1,
            }}
          >
            better ›
          </Typography>
        </Box>
      )}

      {/* Distribution read-out — only in the zoom view (the compact card
          leans on the glyph + tooltips). Factual: n, median, IQR, today. */}
      {big && dist && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 1,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          Your range · n {dist.n} · median {fmtValue(dist.median, row.unit)} · IQR{' '}
          {fmtValue(dist.q1, row.unit)}–{fmtValue(dist.q3, row.unit)}
          {you !== null && <> · this session {fmtValue(you, row.unit)}</>}
        </Typography>
      )}

      {/* Legend — ordered left→right to match each marker's position on
          the track above, so reading the labels follows the same
          sequence as the ticks (You included, wherever its value sits).
          Ties keep insertion order (You before a same-valued cohort). */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: big ? 1.5 : 0.75,
          rowGap: big ? 1 : 0.25,
          mt: big ? 1.5 : 0.75,
          justifyContent: big ? 'center' : 'flex-start',
        }}
      >
        {legendEntries.map((e) => (
          <LegendItem
            key={e.key}
            color={e.color}
            label={e.label}
            value={fmtValue(e.value, row.unit)}
            big={big}
            outline={e.outline}
            faded={e.faded}
          />
        ))}
      </Box>
    </Box>
  );
}

function VerdictChip({ row }: { row: MetricComparisonRow }) {
  const theme = useTheme();
  // Verdict is computed ONLY from statistically significant references —
  // non-significant bars are context, they don't earn an Expert/Elite claim.
  const sigRefs = row.references.filter((r) => r.significant !== false);
  const tier = performanceTier(row.session_value, sigRefs, row.direction);
  if (tier === null) return null;
  const color = tierColor(theme, tier);
  return (
    <Chip
      size="small"
      label={TIER_LABEL[tier]}
      sx={{
        height: 18,
        fontWeight: 700,
        color,
        bgcolor: alpha(color, 0.14),
        '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem' },
      }}
    />
  );
}

/** One metric = a full-width row: name (left) · gauge (middle) · verdict
 *  (right). Reads top-to-bottom like the design mockup rather than as a
 *  grid of side-by-side cards. Double-click zooms it into a dialog. */
function MetricCell({
  row,
  resolve,
  onZoom,
}: {
  row: MetricComparisonRow;
  resolve: CohortResolver;
  onZoom: (row: MetricComparisonRow) => void;
}) {
  return (
    <Box
      onDoubleClick={() => onZoom(row)}
      title="Double-click to enlarge"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.75,
        borderTop: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 'none' },
        cursor: 'zoom-in',
      }}
    >
      {/* Name (fixed-width left column so every gauge starts at the same x). */}
      <Box sx={{ width: { xs: 116, sm: 176, md: 216 }, flexShrink: 0 }}>
        <Tooltip
          arrow
          title={
            <Box sx={{ maxWidth: 260 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {prettyMetric(row.metric)}
                {row.unit ? ` (${row.unit})` : ''}
              </Typography>
              {metricDescription(row.metric) && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                  {metricDescription(row.metric)}
                </Typography>
              )}
            </Box>
          }
        >
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }} noWrap>
            {prettyMetric(row.metric)}
            {row.unit ? ` (${row.unit})` : ''}
          </Typography>
        </Tooltip>
      </Box>

      {/* Gauge fills the middle. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Gauge row={row} resolve={resolve} />
      </Box>

      {/* Verdict pinned right (hidden on the narrowest widths — the bar
          colour already carries the good/warn/bad read). */}
      <Box
        sx={{
          width: 150,
          flexShrink: 0,
          display: { xs: 'none', sm: 'flex' },
          justifyContent: 'flex-end',
        }}
      >
        {row.beats_reference !== null && <VerdictChip row={row} />}
      </Box>
    </Box>
  );
}

/** Zoomed metric view opened on double-clicking a cell. */
function MetricZoomDialog({
  row,
  resolve,
  onClose,
}: {
  row: MetricComparisonRow | null;
  resolve: CohortResolver;
  onClose: () => void;
}) {
  return (
    <Dialog open={row !== null} onClose={onClose} maxWidth="sm" fullWidth>
      {row && (
        <>
          <DialogTitle sx={{ pr: 6 }}>
            {prettyMetric(row.metric)}
            {row.unit ? ` (${row.unit})` : ''}
            <IconButton
              onClick={onClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Gauge row={row} resolve={resolve} big />
            {row.beats_reference !== null && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <VerdictChip row={row} />
              </Box>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

const SECTIONS: {
  key: string;
  label: string;
  icon: React.ReactNode;
  match: (m: MetricComparisonRow) => boolean;
}[] = [
  {
    key: 'motion',
    label: 'Motion (IMU)',
    icon: <MetricGlyph src="/metric-motion.png" />,
    match: (m) => m.modality !== 'eeg',
  },
  {
    key: 'cognitive',
    label: 'Cognitive (EEG)',
    icon: <MetricGlyph src="/metric-eeg.png" />,
    match: (m) => m.modality === 'eeg',
  },
];

/** Title-row key explaining the two orange intensities: bold = the You bar/box
 *  is graded against a statistically-significant benchmark; faded = the
 *  benchmark is indicative only (not significant). */
function SignificanceKey() {
  const swatch = (bg: string) => (
    <Box
      sx={{ width: 16, height: 8, borderRadius: 0.5, bgcolor: bg, flexShrink: 0 }}
    />
  );
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
    >
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        {swatch(YOU_ACCENT)}
        <Typography variant="caption" color="text.secondary">
          Statistically significant
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        {swatch(alpha(YOU_ACCENT, 0.45))}
        <Typography variant="caption" color="text.secondary">
          Not significant (indicative)
        </Typography>
      </Stack>
    </Stack>
  );
}

/** How a participant reads in the "Compare with" picker. */
const compareLabel = (p: Participant) => p.full_name || p.email || 'participant';

export function SessionMetricScorecard({
  tenantId,
  sessionId,
  asParticipant = false,
}: {
  /** Tenant-admin drill-down passes it; the participant's own view omits
   *  it and the /me endpoint resolves the owning tenant. */
  tenantId?: string;
  sessionId: string;
  /** Participant viewing their OWN session (no tenantId): still offer the
   *  single-session + longitudinal PDF reports, routed through the /me
   *  endpoints. The cross-participant compare picker stays staff-only. */
  asParticipant?: boolean;
}) {
  // Reports (single + longitudinal) are available to a tenant admin (tenantId)
  // OR to the participant on their own session — each side routes to its own
  // endpoints via the wrappers below.
  const canReport = !!tenantId || asParticipant;
  const fetchReport = () =>
    tenantId ? getSessionReport(tenantId, sessionId) : getMySessionReport(sessionId);
  const postReport = () =>
    tenantId
      ? requestSessionReport(tenantId, sessionId)
      : requestMySessionReport(sessionId);
  const fetchLongi = () =>
    tenantId
      ? getSessionLongitudinal(tenantId, sessionId)
      : getMySessionLongitudinal(sessionId);
  const fetchLongiReport = () =>
    tenantId
      ? getSessionLongitudinalReport(tenantId, sessionId)
      : getMySessionLongitudinalReport(sessionId);
  const postLongiReport = () =>
    tenantId
      ? requestSessionLongitudinalReport(tenantId, sessionId)
      : requestMySessionLongitudinalReport(sessionId);
  const [data, setData] = useState<SessionMetricComparison | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState<MetricComparisonRow | null>(null);
  // Canonical cohort labels + colours (GET /cohort-tiers) — the single
  // source of truth so the gauge uses the same standard labels the rest
  // of the platform does. Falls back to the baked canonical set.
  const [tiers, setTiers] = useState<CohortTier[]>(FALLBACK_TIERS);
  // PDF report: async render → poll → save. Longitudinal (from the overlay)
  // is the same flow against its own endpoints.
  const report = usePreparedDownload({
    fetchStatus: fetchReport, request: postReport, noun: 'report',
  });
  const longiReport = usePreparedDownload({
    fetchStatus: fetchLongiReport, request: postLongiReport, noun: 'report',
  });
  const { reset: resetReport } = report;
  const { reset: resetLongiReport } = longiReport;
  // Procedure menu + the longitudinal overlay that takes over this panel.
  const [procMenuEl, setProcMenuEl] = useState<null | HTMLElement>(null);
  const [longi, setLongi] = useState<{
    loading: boolean;
    report: LongitudinalReport | null;
    error: string | null;
  } | null>(null);
  // Compare progression across participants (LONGITUDINAL-COMPARE). Options are
  // the tenant's participants; picking one fetches its report for THIS overlay's
  // procedure and adds a column to the compare table.
  const [compareOptions, setCompareOptions] = useState<Participant[]>([]);
  const [compareSel, setCompareSel] = useState<Participant[]>([]);
  const [compareReports, setCompareReports] = useState<
    Record<string, LongitudinalReport>
  >({});

  const openLongitudinal = () => {
    if (!canReport) return;
    setProcMenuEl(null);
    setCompareSel([]);
    setCompareReports({});
    setLongi({ loading: true, report: null, error: null });
    fetchLongi()
      .then((r) => setLongi({ loading: false, report: r, error: null }))
      .catch((e) =>
        setLongi({
          loading: false,
          report: null,
          error: extractErrorMessage(e, 'no progression for this procedure yet'),
        }),
      );
    // The cross-participant compare picker is staff-only — a participant sees
    // only their own progression. Options are best-effort; the picker just
    // stays empty if the roster can't load.
    if (tenantId) {
      listParticipants(tenantId)
        .then((ps) =>
          setCompareOptions(
            // Alphabetical by the label the picker shows.
            [...ps].sort((a, b) =>
              compareLabel(a).localeCompare(compareLabel(b), undefined, {
                sensitivity: 'base',
                numeric: true,
              }),
            ),
          ),
        )
        .catch(() => setCompareOptions([]));
    }
  };

  // Add/remove compare participants: fetch each newly-picked participant's
  // longitudinal report for the overlay's procedure (reusing the single
  // participant endpoint), keyed so re-picking never refetches.
  const onCompareChange = (next: Participant[]) => {
    setCompareSel(next);
    const procId = longi?.report?.procedure_id;
    if (!tenantId || !procId) return;
    for (const p of next) {
      if (compareReports[p.id]) continue;
      getParticipantProcedureLongitudinal(tenantId, p.id, procId)
        .then((r) =>
          setCompareReports((prev) => ({ ...prev, [p.id]: r })),
        )
        .catch(() => {
          /* leave the column absent if this participant has no data */
        });
    }
  };

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    resetReport(); // switching sessions cancels a pending report
    resetLongiReport();
    setLongi(null); // switching sessions closes the progression overlay
    setCompareSel([]);
    setCompareReports({});
    const p = tenantId
      ? getSessionMetricComparison(tenantId, sessionId)
      : getMySessionMetricComparison(sessionId);
    p.then((d) => alive && setData(d)).catch(
      (e) => alive && setError(extractErrorMessage(e, 'failed to load metrics')),
    );
    return () => {
      alive = false;
    };
  }, [tenantId, sessionId]);

  useEffect(() => {
    getCohortTiers()
      .then((t) => t.length > 0 && setTiers(t))
      .catch(() => {
        /* keep the fallback set */
      });
  }, []);

  const resolve = useMemo(() => makeCohortResolver(tiers), [tiers]);

  const anyRef = data?.rows.some((r) => r.references.length > 0) ?? false;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          columnGap: 2,
          rowGap: 0.5,
          mb: 1,
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
          <Typography variant="subtitle2">
            Session metrics vs reference cohorts
          </Typography>
          {(() => {
            const procLabel = data?.procedure_group ?? data?.procedure_name;
            if (!procLabel) return null;
            // The procedure is a menu (Longitudinal reports, and future report
            // types) for anyone who can pull reports — a tenant admin or the
            // participant on their own session. Otherwise it's plain text.
            return canReport ? (
              <Button
                size="small"
                variant="text"
                color="inherit"
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setProcMenuEl(e.currentTarget)}
                sx={{ minWidth: 0, py: 0, textTransform: 'none', fontWeight: 400 }}
              >
                <Typography variant="caption" color="text.secondary">
                  · {procLabel}
                </Typography>
              </Button>
            ) : (
              <Typography component="span" variant="caption" color="text.secondary">
                {' '}
                · {procLabel}
              </Typography>
            );
          })()}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {canReport && data && longi === null && (
            <>
              <PreparedDownloadNote error={report.error} done={report.done} />
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  report.pending ? (
                    <CircularProgress size={14} />
                  ) : (
                    <PictureAsPdfIcon />
                  )
                }
                onClick={() => canReport && void report.start()}
                disabled={report.pending !== null}
              >
                {report.pending ? 'Preparing report…' : 'Download report'}
              </Button>
            </>
          )}
          {anyRef && <SignificanceKey />}
        </Stack>
      </Box>
      <Menu
        anchorEl={procMenuEl}
        open={Boolean(procMenuEl)}
        onClose={() => setProcMenuEl(null)}
      >
        <MenuItem onClick={openLongitudinal}>
          <TimelineIcon fontSize="small" sx={{ mr: 1 }} />
          Longitudinal reports
        </MenuItem>
      </Menu>

      {/* Longitudinal overlay — takes over this panel's real estate. */}
      {longi !== null && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setLongi(null)}
            >
              Back to metrics
            </Button>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              Progression over time
              {data?.procedure_group ?? data?.procedure_name
                ? ` — ${data?.procedure_group ?? data?.procedure_name}`
                : ''}
            </Typography>
            {canReport && longi.report && longi.report.metrics.length > 0 && (
              <>
                <PreparedDownloadNote
                  error={longiReport.error}
                  done={longiReport.done}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    longiReport.pending ? (
                      <CircularProgress size={14} />
                    ) : (
                      <PictureAsPdfIcon />
                    )
                  }
                  onClick={() => canReport && void longiReport.start()}
                  disabled={longiReport.pending !== null}
                >
                  {longiReport.pending ? 'Preparing report…' : 'Download report'}
                </Button>
              </>
            )}
          </Box>
          {longi.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : longi.error ? (
            <Alert severity="info">{longi.error}</Alert>
          ) : longi.report ? (
            <>
              {/* Compare with other participants on the same procedure —
                  staff-only (a participant sees only their own progression). */}
              {tenantId && (
                <Autocomplete
                  multiple
                  size="small"
                  sx={{ mb: 1.5 }}
                  options={compareOptions.filter(
                    (p) => p.id !== longi.report?.participant_user_id,
                  )}
                  value={compareSel}
                  onChange={(_e, next) => onCompareChange(next)}
                  getOptionLabel={compareLabel}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Compare with"
                      placeholder="Add participants…"
                    />
                  )}
                />
              )}
              {compareSel.length > 0 ? (
                <LongitudinalCompareView
                  reports={[
                    longi.report,
                    ...compareSel
                      .map((p) => compareReports[p.id])
                      .filter((r): r is LongitudinalReport => !!r),
                  ]}
                />
              ) : (
                <LongitudinalReportView report={longi.report} />
              )}
            </>
          ) : null}
        </Box>
      )}

      {longi === null && error && <Alert severity="error">{error}</Alert>}
      {longi === null && !data && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {longi === null && data && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5 }}
          >
            Every bar reads left → right as worse → better, so the good end
            (practice-ready / expert) is always on the right — lower-is-better
            metrics are flipped to keep it that way.
          </Typography>
          {!anyRef && (
            <Typography variant="caption" color="text.secondary">
              No reference cohort is mapped for this procedure yet — showing
              this session's values only.
            </Typography>
          )}
          {SECTIONS.map((section) => {
            const rows = data.rows.filter(section.match);
            if (rows.length === 0) return null;
            return (
              // Each modality (Motion / EEG) is its own softly-tinted panel
              // with a labelled header — grouping the metrics into readable
              // sections rather than one long spreadsheet of rows.
              <Paper
                key={section.key}
                elevation={0}
                sx={{
                  mt: 2.5,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ color: 'primary.main', mb: 1 }}
                >
                  {section.icon}
                  <Typography
                    variant="overline"
                    sx={{ fontWeight: 800, letterSpacing: '0.09em' }}
                  >
                    {section.label}
                  </Typography>
                </Stack>
                {/* One metric per full-width row, stacked — reads
                    top-to-bottom like the design mockup. */}
                <Box>
                  {rows.map((r) => (
                    <MetricCell
                      key={r.metric}
                      row={r}
                      resolve={resolve}
                      onZoom={setZoom}
                    />
                  ))}
                </Box>
              </Paper>
            );
          })}
        </>
      )}

      <MetricZoomDialog
        row={zoom}
        resolve={resolve}
        onClose={() => setZoom(null)}
      />
    </Paper>
  );
}
